import { jsx as _jsx } from "react/jsx-runtime";
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as React from 'react';
import { cn } from '../lib/cn';
/**
 * Written out per density rather than assembled, because Tailwind's scanner reads source text and
 * a class built at runtime is never emitted.
 *
 * The thumb's travel is the track's content box minus the thumb: `44 - 2*2 - 20 = 20` for the
 * console, `44 - 3*2 - 20 = 18` for the wizard, `52 - 3*2 - 24 = 22` for the member app. Getting
 * that arithmetic wrong is invisible until the thumb overhangs the track by a pixel in the "on"
 * state only.
 *
 * `on` and `off` are the track's own colours where a density does not want the default pair. They
 * are ignored by `tone="danger"`, which is a state the colour has to shout about and so wins.
 */
const DENSITY_CLASSES = {
    console: {
        track: 'h-6 w-11 border-2',
        thumb: 'h-5 w-5 data-[state=checked]:translate-x-5',
    },
    wizard: {
        track: 'h-[26px] w-11 border-[3px]',
        thumb: 'h-5 w-5 data-[state=checked]:translate-x-[18px]',
    },
    member: {
        track: 'h-[30px] w-[52px] border-[3px]',
        thumb: 'h-6 w-6 data-[state=checked]:translate-x-[22px]',
        on: 'data-[state=checked]:bg-spark',
        off: 'data-[state=unchecked]:bg-border-strong',
    },
};
export const Switch = React.forwardRef(({ className, tone = 'default', density = 'console', ...props }, ref) => {
    const metrics = DENSITY_CLASSES[density];
    return (_jsx(SwitchPrimitive.Root, { ref: ref, className: cn('peer inline-flex shrink-0 cursor-pointer items-center rounded-pill border-transparent transition-colors', metrics.track, 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', metrics.off ?? 'data-[state=unchecked]:bg-muted-foreground/40', tone === 'danger'
            ? 'data-[state=checked]:bg-destructive'
            : (metrics.on ?? 'data-[state=checked]:bg-primary'), className), ...props, children: _jsx(SwitchPrimitive.Thumb, { className: cn('pointer-events-none block rounded-pill bg-card shadow-sm ring-0 transition-transform', metrics.thumb, 'data-[state=unchecked]:translate-x-0') }) }));
});
Switch.displayName = SwitchPrimitive.Root.displayName;
//# sourceMappingURL=switch.js.map